import { getReferenceString, MedplumClient, resolveId, WithId } from "@medplum/core";
import { BundleEntry, List, Observation } from "@medplum/fhirtypes";

import { getCodeableConceptCode, getResourcesFromBundle } from "@/utils/medplum";

import { EDD_CODING_QUERY, EDD_DISPLAY_CODING_QUERY } from "../constants/edd";
import { EDDDisplay } from "../types";
import { isEddWrapperWithValue } from "../utils/edd";

import { fetchPregnancyRecordList } from "./record";

// --- Build -------------------------------------------------------------------

/**
 * Builds `BundleEntry` items that cancel the most recent final EDD wrapper
 * `Observation` and all of its `hasMember` observations via FHIR `PUT`.
 *
 * Each entry replaces the full resource with `status` set to `"cancelled"`.
 * The `hasMember` observations are fetched in a single `searchResources` call
 * using their IDs. Returns an empty array when no matching wrapper exists.
 *
 * @param medplum - Medplum client
 * @param patientReferenceString - Patient reference string used to search for the
 *   existing EDD wrapper (e.g. `"Patient/123"`)
 * @returns Array of `PUT` bundle entries — one for the wrapper followed by one per
 *   `hasMember` observation — or `[]` if no final EDD wrapper is found
 */
export async function buildCancelEDDBundleEntries(
  medplum: MedplumClient,
  patientReferenceString: string,
): Promise<BundleEntry<Observation>[]> {
  const { bundle } = await medplum.searchResources("Observation", {
    code: EDD_CODING_QUERY,
    "status:not": "cancelled,entered-in-error",
    subject: patientReferenceString,
    _count: 1000,
  });

  const existingEddObservations = getResourcesFromBundle("Observation", bundle);

  if (!existingEddObservations.length) return [];

  const entries: BundleEntry<Observation>[] = [];

  for (const existingEddObservation of existingEddObservations) {
    entries.push({
      request: { method: "PUT" as const, url: getReferenceString(existingEddObservation) },
      resource: { ...existingEddObservation, status: "cancelled" as const },
    });

    const memberIds = (existingEddObservation.hasMember ?? [])
      .map((hasMemberReference) => resolveId(hasMemberReference))
      .filter(Boolean) as string[];

    if (memberIds.length > 0) {
      const { bundle } = await medplum.searchResources("Observation", {
        _id: memberIds.join(","),
        _count: memberIds.length,
      });

      const members = getResourcesFromBundle("Observation", bundle) ?? [];

      entries.push(
        ...members.map((member) => ({
          request: { method: "PUT" as const, url: getReferenceString(member) },
          resource: { ...member, status: "cancelled" as const },
        })),
      );
    }
  }

  return entries;
}

// --- Fetch -------------------------------------------------------------------

/**
 * Reads EDD wrapper Observations referenced directly on a pregnancy `List.entry`.
 *
 * Used when `_has:List:item:_id` search misses cancelled EDDs after close-out.
 * Prefers `final`, then `cancelled`, then any other non-error status with a value.
 *
 * @param medplum - Medplum client
 * @param list - Pregnancy record `List` whose entries may reference the EDD Observation
 * @returns Matching EDD wrapper Observation, or `undefined` when none are found
 */
export async function fetchEDDFromPregnancyListEntries(
  medplum: MedplumClient,
  list: List,
): Promise<Observation | undefined> {
  const observationIds = (list.entry ?? []).reduce<string[]>((ids, entry) => {
    if (!entry.item?.reference?.startsWith("Observation/")) return ids;
    const observationId = resolveId(entry.item);
    if (observationId) ids.push(observationId);
    return ids;
  }, []);

  if (observationIds.length === 0) return undefined;

  const response = await medplum.executeBatch({
    resourceType: "Bundle",
    type: "batch",
    entry: observationIds.map((observationId) => ({
      request: { method: "GET" as const, url: `Observation/${observationId}` },
    })),
  });

  const eddObservations = (response.entry ?? []).reduce<WithId<Observation>[]>((matched, entry) => {
    const status = entry.response?.status ?? "";
    if (status && !status.startsWith("2")) return matched;

    const observation = entry.resource;
    if (observation?.resourceType === "Observation" && isEddWrapperWithValue(observation)) {
      matched.push(observation as WithId<Observation>);
    }
    return matched;
  }, []);

  return (
    eddObservations.find((observation) => observation.status === "final") ??
    eddObservations.find((observation) => observation.status === "cancelled") ??
    eddObservations[0]
  );
}

/**
 * Fetches the most recent final EDD `Observation` linked to a pregnancy `List`.
 * Falls back to cancelled status, then to Observations referenced on `list.entry`
 * when provided (covers post-close-out historical GA).
 *
 * @param medplum - Medplum client
 * @param pregnancyRecordId - FHIR `List` resource ID representing the pregnancy record
 * @param list - Optional pregnancy `List` used for entry-based fallback when search misses
 * @returns The most recent EDD `Observation`, or `undefined` if none is found
 */
export async function fetchEDDForPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecordId: string,
  list?: List,
) {
  const baseQuery = {
    "_has:List:item:_id": pregnancyRecordId,
    code: EDD_CODING_QUERY,
    _sort: "-_lastUpdated",
  };

  const activeEdd = await medplum.searchOne("Observation", { ...baseQuery, status: "final" });

  if (activeEdd) return activeEdd;

  const cancelledEdd = await medplum.searchOne("Observation", {
    ...baseQuery,
    status: "cancelled",
  });

  if (cancelledEdd) return cancelledEdd;

  if (list) {
    return fetchEDDFromPregnancyListEntries(medplum, list);
  }

  return undefined;
}

/**
 * Returns the currently active `EDDDisplay` method for a pregnancy record by
 * inspecting the EDD wrapper's `hasMember` observations. Returns `undefined`
 * when no active EDD exists or when no display observation has been set.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecordId - FHIR `List` resource ID representing the pregnancy record
 * @returns The active `EDDDisplay` enum value, or `undefined` if not set
 */
export async function fetchEDDDisplayForPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecordId: string,
): Promise<EDDDisplay | undefined> {
  const eddDisplayObservation = await medplum.searchOne("Observation", {
    "_has:List:item:_id": pregnancyRecordId,
    code: EDD_DISPLAY_CODING_QUERY,
    status: "final",
    _sort: "-_lastUpdated",
  });

  return getCodeableConceptCode(eddDisplayObservation?.valueCodeableConcept) as
    | EDDDisplay
    | undefined;
}

export async function fetchEDDDisplayForCurrentPregnancyRecord(
  medplum: MedplumClient,
  patientReferenceString: string,
): Promise<EDDDisplay | undefined> {
  const currentPregnancyRecord = (
    await fetchPregnancyRecordList(medplum, patientReferenceString, {
      status: "current",
    })
  )?.[0];

  if (!currentPregnancyRecord?.id) return undefined;

  return fetchEDDDisplayForPregnancyRecord(medplum, currentPregnancyRecord.id);
}
